import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    FileText,
    Loader2,
    Save,
    Trash2,
    Plus,
    Eye,
    EyeOff,
    Image as ImageIcon,
    Upload,
    ExternalLink,
    Star,
    StarOff,
    FolderOpen,
    Check,
    X,
    RefreshCw
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface GalleryImage {
    name: string;
    url: string;
    created_at: string;
}

interface Article {
    id: string;
    title: string;
    title_en: string | null;
    slug: string;
    excerpt: string | null;
    excerpt_en: string | null;
    content: string;
    content_en: string | null;
    cover_image: string | null;
    is_published: boolean;
    is_featured: boolean;
    published_at: string | null;
    created_at: string;
    updated_at: string;
    view_count: number;
}

const ArticleManagement = () => {
    const { toast } = useToast();
    const [articles, setArticles] = useState<Article[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [editId, setEditId] = useState<string | null>(null);
    const [title, setTitle] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [excerpt, setExcerpt] = useState("");
    const [excerptEn, setExcerptEn] = useState("");
    const [content, setContent] = useState("");
    const [contentEn, setContentEn] = useState("");
    const [coverImage, setCoverImage] = useState("");
    const [isPublished, setIsPublished] = useState(false);
    const [isFeatured, setIsFeatured] = useState(false);

    // Gallery modal state
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
    const [loadingGallery, setLoadingGallery] = useState(false);

    // Quill editor modules
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['blockquote', 'code-block'],
            ['clean']
        ],
    };

    const fetchArticles = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await (supabase as any)
                .from("articles")
                .select("*")
                .order("updated_at", { ascending: false });

            if (error) throw error;
            setArticles(data || []);
        } catch (err: any) {
            console.error("Error fetching articles:", err);
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchArticles();
    }, []);

    const resetForm = () => {
        setEditId(null);
        setTitle("");
        setTitleEn("");
        setExcerpt("");
        setExcerptEn("");
        setContent("");
        setContentEn("");
        setCoverImage("");
        setIsPublished(false);
        setIsFeatured(false);
    };

    const handleEdit = (article: Article) => {
        setEditId(article.id);
        setTitle(article.title);
        setTitleEn(article.title_en || "");
        setExcerpt(article.excerpt || "");
        setExcerptEn(article.excerpt_en || "");
        setContent(article.content);
        setContentEn(article.content_en || "");
        setCoverImage(article.cover_image || "");
        setIsPublished(article.is_published);
        setIsFeatured(article.is_featured);

        // Scroll to top of form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const generateSlug = (text: string) => {
        return text
            .toLowerCase()
            .trim()
            .replace(/[^\u0E00-\u0E7Fa-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast({
                title: "Error",
                description: "กรุณาเลือกไฟล์รูปภาพเท่านั้น",
                variant: "destructive",
            });
            return;
        }

        setIsUploading(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `article-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
            const filePath = `articles/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('generation-inputs')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from('generation-inputs')
                .getPublicUrl(filePath);

            setCoverImage(publicUrl);
            toast({ title: "อัพโหลดสำเร็จ", description: "อัพโหลดรูปภาพเรียบร้อยแล้ว" });
        } catch (err: any) {
            console.error("Upload error:", err);
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setIsUploading(false);
        }
    };

    const fetchGalleryImages = async () => {
        setLoadingGallery(true);
        try {
            const { data, error } = await supabase.storage
                .from('generation-inputs')
                .list('articles', {
                    limit: 100,
                    sortBy: { column: 'created_at', order: 'desc' }
                });

            if (error) throw error;

            const images: GalleryImage[] = (data || [])
                .filter(file => file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i))
                .map(file => {
                    const { data: { publicUrl } } = supabase.storage
                        .from('generation-inputs')
                        .getPublicUrl(`articles/${file.name}`);
                    return {
                        name: file.name,
                        url: publicUrl,
                        created_at: file.created_at || ''
                    };
                });

            setGalleryImages(images);
        } catch (err: any) {
            console.error("Error fetching gallery:", err);
            toast({
                title: "Error",
                description: "ไม่สามารถโหลดรูปภาพจาก Gallery ได้",
                variant: "destructive",
            });
        } finally {
            setLoadingGallery(false);
        }
    };

    const openGallery = () => {
        setShowGalleryModal(true);
        fetchGalleryImages();
    };

    const selectGalleryImage = (url: string) => {
        setCoverImage(url);
        setShowGalleryModal(false);
        toast({ title: "เลือกรูปสำเร็จ", description: "เลือกรูปภาพจาก Gallery เรียบร้อยแล้ว" });
    };

    const handleSave = async () => {
        if (!title.trim()) {
            toast({
                title: "Error",
                description: "กรุณากรอกหัวข้อบทความ",
                variant: "destructive",
            });
            return;
        }

        if (!content.trim()) {
            toast({
                title: "Error",
                description: "กรุณากรอกเนื้อหาบทความ",
                variant: "destructive",
            });
            return;
        }

        setIsSaving(true);
        try {
            const slug = generateSlug(title) + '-' + Date.now().toString(36);

            const payload = {
                title: title.trim(),
                title_en: titleEn.trim() || null,
                slug: editId ? undefined : slug, // Only set slug for new articles
                excerpt: excerpt.trim() || null,
                excerpt_en: excerptEn.trim() || null,
                content: content,
                content_en: contentEn || null,
                cover_image: coverImage || null,
                is_published: isPublished,
                is_featured: isFeatured,
                published_at: isPublished ? new Date().toISOString() : null,
            };

            if (editId) {
                // Remove slug from update payload
                const { slug: _, ...updatePayload } = payload as any;

                const { error } = await (supabase as any)
                    .from("articles")
                    .update(updatePayload)
                    .eq("id", editId);

                if (error) throw error;
                toast({ title: "บันทึกสำเร็จ", description: "อัปเดตบทความเรียบร้อยแล้ว" });
            } else {
                const { error } = await (supabase as any)
                    .from("articles")
                    .insert(payload);

                if (error) throw error;
                toast({ title: "สร้างสำเร็จ", description: "สร้างบทความใหม่เรียบร้อยแล้ว" });
            }

            resetForm();
            fetchArticles();
        } catch (err: any) {
            console.error("Error saving article:", err);
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm("คุณแน่ใจหรือไม่ที่จะลบบทความนี้?")) return;

        try {
            const { error } = await (supabase as any)
                .from("articles")
                .delete()
                .eq("id", id);

            if (error) throw error;
            toast({ title: "ลบสำเร็จ", description: "ลบบทความเรียบร้อยแล้ว" });
            fetchArticles();
            if (editId === id) resetForm();
        } catch (err: any) {
            console.error("Error deleting article:", err);
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        }
    };

    const handleTogglePublish = async (id: string, currentStatus: boolean) => {
        try {
            const { error } = await (supabase as any)
                .from("articles")
                .update({
                    is_published: !currentStatus,
                    published_at: !currentStatus ? new Date().toISOString() : null
                })
                .eq("id", id);

            if (error) throw error;
            toast({
                title: !currentStatus ? "เผยแพร่แล้ว" : "ยกเลิกเผยแพร่",
                description: !currentStatus ? "บทความถูกเผยแพร่แล้ว" : "บทความถูกซ่อนแล้ว",
            });
            fetchArticles();
        } catch (err: any) {
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        }
    };

    const handleToggleFeatured = async (id: string, currentStatus: boolean) => {
        try {
            const { error } = await (supabase as any)
                .from("articles")
                .update({ is_featured: !currentStatus })
                .eq("id", id);

            if (error) throw error;
            toast({
                title: !currentStatus ? "ตรึงบทความ" : "ยกเลิกตรึง",
                description: !currentStatus ? "บทความจะแสดงใน Dashboard" : "บทความจะไม่แสดงใน Dashboard",
            });
            fetchArticles();
        } catch (err: any) {
            toast({
                title: "Error",
                description: err.message,
                variant: "destructive",
            });
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString("th-TH", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <div className="space-y-6">
            {/* Form Card */}
            <Card variant="glass">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        {editId ? "แก้ไขบทความ" : "สร้างบทความใหม่"}
                    </CardTitle>
                    <CardDescription>
                        บทความจะแสดงในหน้า Dashboard และสามารถเข้าถึงได้ผ่าน URL
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Title */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="title">หัวข้อ (ภาษาไทย) *</Label>
                            <Input
                                id="title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="เช่น: แนะนำเครื่องมือ Gen VDO ไทม์แลปก่อสร้าง"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="titleEn">หัวข้อ (English)</Label>
                            <Input
                                id="titleEn"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                placeholder="e.g. Introducing Construction Timelapse Tool"
                            />
                        </div>
                    </div>

                    {/* Excerpt */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="excerpt">สรุปสั้นๆ (ภาษาไทย)</Label>
                            <Textarea
                                id="excerpt"
                                value={excerpt}
                                onChange={(e) => setExcerpt(e.target.value)}
                                placeholder="สรุปเนื้อหาสั้นๆ สำหรับแสดงในหน้า Dashboard..."
                                rows={2}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="excerptEn">สรุปสั้นๆ (English)</Label>
                            <Textarea
                                id="excerptEn"
                                value={excerptEn}
                                onChange={(e) => setExcerptEn(e.target.value)}
                                placeholder="Short summary for Dashboard..."
                                rows={2}
                            />
                        </div>
                    </div>

                    {/* Cover Image */}
                    <div className="space-y-2">
                        <Label>ภาพประกอบบทความ</Label>
                        <div className="flex items-center gap-4">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleImageUpload}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading}
                                className="gap-2"
                            >
                                {isUploading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Upload className="h-4 w-4" />
                                )}
                                อัพโหลดรูปภาพ
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={openGallery}
                                className="gap-2"
                            >
                                <FolderOpen className="h-4 w-4" />
                                เลือกจาก Gallery
                            </Button>
                            <Input
                                value={coverImage}
                                onChange={(e) => setCoverImage(e.target.value)}
                                placeholder="หรือวาง URL รูปภาพ..."
                                className="flex-1"
                            />
                        </div>
                        {coverImage && (
                            <div className="mt-2 relative w-full max-w-md rounded-lg overflow-hidden border border-border">
                                <img src={coverImage} alt="Cover" className="w-full h-auto" />
                                <Button
                                    variant="destructive"
                                    size="icon"
                                    className="absolute top-2 right-2 h-8 w-8"
                                    onClick={() => setCoverImage("")}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Content - WYSIWYG Editor */}
                    <div className="space-y-2">
                        <Label>เนื้อหา (ภาษาไทย) *</Label>
                        <div className="bg-white dark:bg-gray-900 rounded-lg border border-border overflow-hidden">
                            <ReactQuill
                                theme="snow"
                                value={content}
                                onChange={setContent}
                                modules={modules}
                                placeholder="เขียนเนื้อหาบทความที่นี่..."
                                className="min-h-[300px]"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>เนื้อหา (English)</Label>
                        <div className="bg-white dark:bg-gray-900 rounded-lg border border-border overflow-hidden">
                            <ReactQuill
                                theme="snow"
                                value={contentEn}
                                onChange={setContentEn}
                                modules={modules}
                                placeholder="Write article content here..."
                                className="min-h-[200px]"
                            />
                        </div>
                    </div>

                    {/* Settings */}
                    <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={isPublished}
                                onCheckedChange={setIsPublished}
                            />
                            <span className={isPublished ? "text-green-500 font-medium" : "text-muted-foreground"}>
                                {isPublished ? "เผยแพร่" : "ฉบับร่าง"}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={isFeatured}
                                onCheckedChange={setIsFeatured}
                            />
                            <span className={isFeatured ? "text-amber-500 font-medium" : "text-muted-foreground"}>
                                {isFeatured ? "แสดงใน Dashboard" : "ไม่แสดงใน Dashboard"}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button onClick={handleSave} disabled={isSaving} className="gap-2">
                            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {editId ? "บันทึกการแก้ไข" : "สร้างบทความ"}
                        </Button>
                        {editId && (
                            <Button variant="outline" onClick={resetForm}>
                                ยกเลิก
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Articles List */}
            <Card variant="glass">
                <CardHeader>
                    <CardTitle>รายการบทความทั้งหมด ({articles.length})</CardTitle>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : articles.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>ยังไม่มีบทความ</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {articles.map((article) => (
                                <div
                                    key={article.id}
                                    className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors border border-border/50"
                                >
                                    <div className="flex items-start gap-4 flex-1">
                                        {article.cover_image ? (
                                            <img
                                                src={article.cover_image}
                                                alt={article.title}
                                                className="w-20 h-14 object-cover rounded-lg shrink-0"
                                            />
                                        ) : (
                                            <div className="w-20 h-14 bg-muted rounded-lg flex items-center justify-center shrink-0">
                                                <ImageIcon className="h-6 w-6 text-muted-foreground" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <p className="font-medium truncate">{article.title}</p>
                                                {article.is_featured && (
                                                    <Badge variant="secondary" className="bg-amber-500/20 text-amber-600">
                                                        Featured
                                                    </Badge>
                                                )}
                                                {article.is_published ? (
                                                    <Badge variant="secondary" className="bg-green-500/20 text-green-600">
                                                        เผยแพร่
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary">ฉบับร่าง</Badge>
                                                )}
                                            </div>
                                            {article.excerpt && (
                                                <p className="text-sm text-muted-foreground truncate">{article.excerpt}</p>
                                            )}
                                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                                <span>อัปเดต: {formatDate(article.updated_at)}</span>
                                                <span>👁️ {article.view_count}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleToggleFeatured(article.id, article.is_featured)}
                                            className={article.is_featured ? "text-amber-500" : "text-muted-foreground"}
                                            title={article.is_featured ? "ยกเลิกตรึง" : "ตรึงใน Dashboard"}
                                        >
                                            {article.is_featured ? <Star className="h-4 w-4 fill-current" /> : <StarOff className="h-4 w-4" />}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleTogglePublish(article.id, article.is_published)}
                                            className={article.is_published ? "text-green-500" : "text-muted-foreground"}
                                            title={article.is_published ? "ยกเลิกเผยแพร่" : "เผยแพร่"}
                                        >
                                            {article.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                        </Button>
                                        {article.is_published && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                asChild
                                                title="ดูหน้าบทความ"
                                            >
                                                <a href={`/article/${article.id}`} target="_blank" rel="noopener noreferrer">
                                                    <ExternalLink className="h-4 w-4" />
                                                </a>
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleEdit(article)}
                                        >
                                            แก้ไข
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDelete(article.id)}
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Gallery Modal */}
            <Dialog open={showGalleryModal} onOpenChange={setShowGalleryModal}>
                <DialogContent className="max-w-4xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FolderOpen className="h-5 w-5" />
                            เลือกรูปภาพจาก Gallery
                        </DialogTitle>
                        <DialogDescription>
                            เลือกรูปภาพที่อัพโหลดไว้แล้วสำหรับใช้เป็นภาพประกอบบทความ
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end mb-4">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchGalleryImages}
                            disabled={loadingGallery}
                            className="gap-2"
                        >
                            <RefreshCw className={`h-4 w-4 ${loadingGallery ? 'animate-spin' : ''}`} />
                            รีเฟรช
                        </Button>
                    </div>
                    {loadingGallery ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : galleryImages.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <ImageIcon className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>ยังไม่มีรูปภาพใน Gallery</p>
                            <p className="text-sm mt-2">อัพโหลดรูปภาพใหม่เพื่อใช้เป็นภาพประกอบ</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-[50vh] overflow-y-auto p-1">
                            {galleryImages.map((image) => (
                                <div
                                    key={image.name}
                                    className="relative group cursor-pointer rounded-lg overflow-hidden border border-border hover:border-primary transition-colors"
                                    onClick={() => selectGalleryImage(image.url)}
                                >
                                    <img
                                        src={image.url}
                                        alt={image.name}
                                        className="w-full h-24 object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <Check className="h-8 w-8 text-white" />
                                    </div>
                                    {coverImage === image.url && (
                                        <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-1">
                                            <Check className="h-3 w-3" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Quill Editor Styles */}
            <style>{`
                .ql-container {
                    min-height: 200px;
                    font-size: 16px;
                }
                .ql-editor {
                    min-height: 200px;
                }
                .dark .ql-toolbar {
                    border-color: hsl(var(--border));
                    background: hsl(var(--muted));
                }
                .dark .ql-container {
                    border-color: hsl(var(--border));
                }
                .dark .ql-editor {
                    color: hsl(var(--foreground));
                }
                .dark .ql-stroke {
                    stroke: hsl(var(--muted-foreground));
                }
                .dark .ql-fill {
                    fill: hsl(var(--muted-foreground));
                }
                .dark .ql-picker-label {
                    color: hsl(var(--muted-foreground));
                }
            `}</style>
        </div>
    );
};

export default ArticleManagement;
